import type { PostCreateStorefrontReviewVariables } from '@api/graphql/reviews/postAuthedStorefrontReview';
import type {
	ReviewFormSubmission,
	ReviewPhoto,
} from '@components/reviews/components/reviews_modal_ver_two/types';
import { Logger } from '@utils/logger/logger';
import Joi from 'joi';

const querySchema = Joi.object<Required<ReviewFormSubmission>>({
	content: Joi.string().min(30).required(),
	firstName: Joi.string().optional(),
	lastName: Joi.string().optional(),
	weddingDate: Joi.string().optional(),
	gallery: Joi.array()
		.items(
			Joi.object({
				source: Joi.object(),
				sourceId: Joi.string(),
				typeCode: Joi.string(),
				height: Joi.number().optional(),
				width: Joi.number().optional(),
			}),
		)
		.optional(),
	rating: Joi.number().integer().min(1).max(5).required(),
	budget: Joi.object({
		cost: Joi.number().optional(),
		guestCount: Joi.number().optional(),
	}).optional(),
	email: Joi.string().allow('').optional(),
	vendorHired: Joi.boolean().optional(),
	vendorFoundOnTK: Joi.boolean().optional(),
});

const ratingsTypes = {
	responseTime: '0495589c-f9e4-4d59-8266-e7e20e454851',
	quality: '176e4e0a-73c9-49a3-ad20-8a77fa004413',
	value: '5089222e-b87d-4ee2-841e-071440b37e86',
	flexibility: '51b279f9-559e-4149-a01c-a4a22d03f179',
	professionalism: 'f24e2d90-3aec-48a2-97ef-eb27264edebe',
};

const mapRating = (rating: number) => {
	return Object.entries(ratingsTypes).map(([_, value]) => ({
		ratingId: value,
		value: rating,
	}));
};

const mapPhotos = (gallery: ReviewPhoto[]) => {
	return gallery.map((photo, index) => ({
		sourceId: photo.sourceId,
		sortOrder: gallery.length - index,
		width: photo.width || null,
		height: photo.height || null,
	}));
};

export const buildVariablesForPostAuthedStorefrontReview = async (
	storefrontId: string,
	memberId: string,
	formData: ReviewFormSubmission,
): Promise<PostCreateStorefrontReviewVariables> => {
	try {
		const {
			content,
			firstName,
			lastName,
			gallery,
			rating,
			budget,
			weddingDate,
		} = await querySchema.validateAsync(formData);

		return {
			id: storefrontId,
			memberId,
			content,
			firstName,
			lastName,
			photos: mapPhotos(gallery),
			ratings: mapRating(rating),
			source: {},
			recommended: false,
			attributes: {
				budgetGuestCount: budget?.guestCount,
				budgetCost: budget?.cost,
				weddingDate,
			},
		};
	} catch (e) {
		Logger.error({
			errorToLog: e,
			newRelic: true,
			log_message:
				'Error in buildVariables function for postAuthedStorefrontReview',
		});
		throw e;
	}
};
